import { OUTLINE, shadow, shade as shadeHex, type Ctx } from './paint';
import { drawCharacterHD } from './character';

export type HairStyle = 'short' | 'long' | 'curly' | 'pony' | 'bun' | 'cap' | 'pigtails' | 'messy' | 'neat';
export type Facing = 'down' | 'up' | 'left' | 'right';

export interface CharLook {
  skin: string;
  hair: string;
  hairStyle: HairStyle;
  top: string;
  topShade: string;
  bottom: string;
  shoes: string;
  /** Child proportions (shorter legs and torso). */
  small?: boolean;
  dress?: boolean;
  glasses?: boolean;
  apron?: string;
  tie?: string;
  /** Cap colour for the `cap` hair style. */
  hat?: string;
  badge?: string;
}

export interface Pose {
  facing: Facing;
  /** Walk cycle phase in [0, 1). */
  phase: number;
  moving: boolean;
  blink: boolean;
  /** Idle breathing in [0, 1). */
  breath: number;
  /** Lying down (collapsed). */
  down?: boolean;
  /** Height above the ground while jumping (px). */
  z?: number;
}

type Part = [color: string, x: number, y: number, w: number, h: number];

/** Player looks selectable on the start screen. */
export const PLAYER_LOOKS: readonly CharLook[] = [
  { skin: '#f6cfa8', hair: '#6b3f22', hairStyle: 'short', top: '#f4f1ea', topShade: '#cfc8bb', bottom: '#2f4a8a', shoes: '#3b2416', badge: '#e2493b' },
  { skin: '#f3c9a0', hair: '#1d1a22', hairStyle: 'long', top: '#f4f1ea', topShade: '#cfc8bb', bottom: '#2f4a8a', shoes: '#3b2416', badge: '#e2493b', dress: true },
  { skin: '#e8b48c', hair: '#a8432a', hairStyle: 'curly', top: '#f4f1ea', topShade: '#cfc8bb', bottom: '#2f4a8a', shoes: '#3b2416', badge: '#e2493b' },
  { skin: '#f8d8bc', hair: '#e2b45a', hairStyle: 'pony', top: '#f4f1ea', topShade: '#cfc8bb', bottom: '#2f4a8a', shoes: '#3b2416', badge: '#e2493b', dress: true },
];

export const CAST: Record<
  'chunbo' | 'teacher' | 'emma' | 'xiaoyu' | 'mom' | 'sister' | 'dad' | 'librarian' | 'rain' | 'coach' | 'clerk' | 'grandma' | 'bo'
  | 'nurse' | 'musicTeacher' | 'lifeguard' | 'barista' | 'giftClerk' | 'deptClerk' | 'waiter' | 'stableKeeper'
  | 'boMom' | 'teaVendor' | 'chickenVendor' | 'gameVendor' | 'shopper1' | 'shopper2' | 'shopper3'
  | 'stationClerk' | 'guide' | 'busker' | 'docent' | 'ranger' | 'hiker' | 'mochiAuntie' | 'captain' | 'receptionist'
  // phase D classmates (classroom package)
  | 'xiaohua' | 'xiaomei' | 'azhe' | 'xiaoqiang' | 'xiaofang',
  CharLook
> = {
  chunbo: { skin: '#e2b089', hair: '#c9c4bc', hairStyle: 'cap', hat: '#26365e', top: '#33477a', topShade: '#26365e', bottom: '#2a2f3d', shoes: '#1d1814', badge: '#f2c94c' },
  teacher: { skin: '#f3cdaa', hair: '#3a2418', hairStyle: 'bun', top: '#4f9a6e', topShade: '#3b7a55', bottom: '#5b4636', shoes: '#2b1d14', glasses: true, dress: true },
  emma: { skin: '#f8dcc4', hair: '#e8c25e', hairStyle: 'long', top: '#ef86a8', topShade: '#cc6688', bottom: '#5a6fb0', shoes: '#f4f1ea' },
  xiaoyu: { skin: '#eac19c', hair: '#18161c', hairStyle: 'messy', top: '#3d4a5c', topShade: '#2c3746', bottom: '#2a2f3d', shoes: '#c9c4bc' },
  mom: { skin: '#f3cba6', hair: '#4a2a1a', hairStyle: 'bun', top: '#9b5fc0', topShade: '#7a4a99', bottom: '#9b5fc0', shoes: '#4a2a1a', dress: true, apron: '#f6efe2' },
  sister: { skin: '#f8d6b8', hair: '#3a2418', hairStyle: 'pigtails', top: '#ff7a7a', topShade: '#d95a5a', bottom: '#ff7a7a', shoes: '#f4f1ea', small: true, dress: true },
  dad: { skin: '#e9bd96', hair: '#2a1d16', hairStyle: 'neat', top: '#dfe8f2', topShade: '#b9c6d4', bottom: '#2f3442', shoes: '#1d1814', tie: '#c0392b' },
  librarian: { skin: '#f0c8a4', hair: '#8f8a86', hairStyle: 'bun', top: '#8a3a4a', topShade: '#6b2a38', bottom: '#4a3a52', shoes: '#2b1d14', glasses: true, dress: true },
  rain: { skin: '#f6d2b0', hair: '#141218', hairStyle: 'short', top: '#ffd34e', topShade: '#d9a82a', bottom: '#3d5a80', shoes: '#f4f1ea' },
  coach: { skin: '#d9a47a', hair: '#1c1814', hairStyle: 'neat', top: '#d0453a', topShade: '#a8261c', bottom: '#d0453a', shoes: '#f4f1ea', badge: '#f4f1ea' },
  clerk: { skin: '#e8b48c', hair: '#2a1d16', hairStyle: 'cap', hat: '#2fa36a', top: '#3fa36f', topShade: '#2a7a50', bottom: '#2f3442', shoes: '#1d1814', apron: '#f4f1ea' },
  grandma: { skin: '#f0c8a4', hair: '#f4f1ea', hairStyle: 'bun', top: '#7d5aa8', topShade: '#5e4282', bottom: '#5e4282', shoes: '#4a2a1a', dress: true, glasses: true },
  bo: { skin: '#f3c9a0', hair: '#3a2418', hairStyle: 'cap', hat: '#e2493b', top: '#ffd34e', topShade: '#d9a82a', bottom: '#3a8fd9', shoes: '#e2493b', small: true },
  nurse: { skin: '#f3cba6', hair: '#2a1d16', hairStyle: 'bun', top: '#ffffff', topShade: '#dfe8ee', bottom: '#cfe8f2', shoes: '#ffffff', dress: true, badge: '#e2493b' },
  musicTeacher: { skin: '#e9bd96', hair: '#3a2418', hairStyle: 'neat', top: '#7d5aa8', topShade: '#5e4282', bottom: '#2f3442', shoes: '#1d1814', glasses: true },
  lifeguard: { skin: '#d9a47a', hair: '#e8c25e', hairStyle: 'pony', top: '#e2493b', topShade: '#b83a2e', bottom: '#2f3442', shoes: '#ffd34e' },
  barista: { skin: '#f3cba6', hair: '#6b3f22', hairStyle: 'short', top: '#f4f1ea', topShade: '#d8d2c4', bottom: '#5e381f', shoes: '#2b1d14', apron: '#8a5530' },
  giftClerk: { skin: '#f8d6b8', hair: '#c45a8a', hairStyle: 'long', top: '#ffd34e', topShade: '#d9a82a', bottom: '#ff9fc0', shoes: '#ffffff', dress: true },
  deptClerk: { skin: '#e8b48c', hair: '#1c1814', hairStyle: 'neat', top: '#2f3442', topShade: '#1d2028', bottom: '#2f3442', shoes: '#1d1814', tie: '#ffd34e' },
  waiter: { skin: '#f0c8a4', hair: '#2a1d16', hairStyle: 'short', top: '#ffffff', topShade: '#dcd6cc', bottom: '#1d1814', shoes: '#1d1814', tie: '#1d1814' },
  boMom: { skin: '#f0c49c', hair: '#2a1d16', hairStyle: 'long', top: '#57a06a', topShade: '#3f7f52', bottom: '#2f3442', shoes: '#ffffff', apron: '#f2e6cf' },
  teaVendor: { skin: '#e8b48c', hair: '#3a2418', hairStyle: 'bun', top: '#e8735a', topShade: '#c45a44', bottom: '#2f3442', shoes: '#1d1814', apron: '#fff6e6' },
  chickenVendor: { skin: '#d9a47a', hair: '#1c1814', hairStyle: 'cap', hat: '#ffd34e', top: '#f4f1ea', topShade: '#d8d2c4', bottom: '#2f3442', shoes: '#1d1814', apron: '#c0392b' },
  gameVendor: { skin: '#e2b089', hair: '#9a948a', hairStyle: 'short', top: '#9b5fc0', topShade: '#7a4a9a', bottom: '#2a2f3d', shoes: '#1d1814', glasses: true },
  shopper1: { skin: '#f3cba6', hair: '#6b3f22', hairStyle: 'pony', top: '#3a8fd9', topShade: '#2a6fb0', bottom: '#f4f1ea', shoes: '#ffffff' },
  shopper2: { skin: '#e9bd96', hair: '#1c1814', hairStyle: 'neat', top: '#ffd34e', topShade: '#d9a82a', bottom: '#2f3442', shoes: '#1d1814', small: true },
  shopper3: { skin: '#d9a47a', hair: '#2a1d16', hairStyle: 'short', top: '#57c278', topShade: '#3f9a5a', bottom: '#5b4636', shoes: '#2b1d14' },
  stationClerk: { skin: '#f0c8a4', hair: '#1c1814', hairStyle: 'neat', top: '#2f4a8a', topShade: '#22386a', bottom: '#2f3442', shoes: '#1d1814', hat: '#2f4a8a', tie: '#e2493b' },
  guide: { skin: '#e9bd96', hair: '#3a2418', hairStyle: 'pony', top: '#ff9f43', topShade: '#d9822a', bottom: '#2f3442', shoes: '#ffffff', badge: '#57c278' },
  busker: { skin: '#d9a47a', hair: '#9a948a', hairStyle: 'short', top: '#5a3a24', topShade: '#3a2416', bottom: '#2f3442', shoes: '#1d1814', glasses: true },
  docent: { skin: '#f3cba6', hair: '#2a1d16', hairStyle: 'bun', top: '#7a2a22', topShade: '#5a1a12', bottom: '#2f3442', shoes: '#1d1814', dress: true, badge: '#ffd34e' },
  ranger: { skin: '#c88a5a', hair: '#1c1814', hairStyle: 'cap', hat: '#4a7a3a', top: '#5a8a4a', topShade: '#3f6a34', bottom: '#5b4636', shoes: '#2b1d14', badge: '#ffd34e' },
  hiker: { skin: '#f3cba6', hair: '#6b3f22', hairStyle: 'cap', hat: '#ffd34e', top: '#3a8fd9', topShade: '#2a6fb0', bottom: '#2f3442', shoes: '#8a5530' },
  mochiAuntie: { skin: '#e8b48c', hair: '#2a1d16', hairStyle: 'bun', top: '#ff9fc0', topShade: '#d97a9a', bottom: '#2f3442', shoes: '#1d1814', apron: '#ffffff' },
  captain: { skin: '#c88a5a', hair: '#e8e4da', hairStyle: 'cap', hat: '#f4f1ea', top: '#2f4a8a', topShade: '#22386a', bottom: '#f4f1ea', shoes: '#1d1814', badge: '#ffd34e' },
  receptionist: { skin: '#f3cba6', hair: '#2a1d16', hairStyle: 'bun', top: '#2f8fa8', topShade: '#22707f', bottom: '#2f3442', shoes: '#1d1814', tie: '#ffd34e' },
  stableKeeper: { skin: '#d9a47a', hair: '#8f8a86', hairStyle: 'cap', hat: '#c9a36a', top: '#6f8f5a', topShade: '#506a40', bottom: '#5b4636', shoes: '#4a2a16' },
  // --- Classmates (phase D): each one readable at a glance -------------------------------------------
  /** Xiaohua: bed-head hair, sky-blue shirt, mismatched red trainers — always in a rush. */
  xiaohua: { skin: '#f3c9a0', hair: '#3a2418', hairStyle: 'messy', top: '#8fc8f0', topShade: '#629fd0', bottom: '#2f4a8a', shoes: '#e2493b' },
  /** Xiaomei: long hair, round glasses, a soft lilac cardigan dress — the book lover. */
  xiaomei: { skin: '#f8d8bc', hair: '#2a1d22', hairStyle: 'long', top: '#c6a8e6', topShade: '#9f80c4', bottom: '#6f58a0', shoes: '#f4f1ea', glasses: true, dress: true },
  /** Azhe: curly hair, loud orange tee with a star badge — the class joker. */
  azhe: { skin: '#e2b089', hair: '#5a3420', hairStyle: 'curly', top: '#ff9f43', topShade: '#d9782a', bottom: '#3a3f4f', shoes: '#57c278', badge: '#ffd34e' },
  /** Xiaoqiang: blue sports cap and green football jersey. */
  xiaoqiang: { skin: '#d9a47a', hair: '#1c1814', hairStyle: 'cap', hat: '#2f7fd9', top: '#2fa36a', topShade: '#227a4e', bottom: '#1d2028', shoes: '#f4f1ea', badge: '#f4f1ea' },
  /** Xiaofang: high ponytail and a red track jacket over yellow shorts. */
  xiaofang: { skin: '#f0c49c', hair: '#141218', hairStyle: 'pony', top: '#e2493b', topShade: '#b83a2e', bottom: '#f2b632', shoes: '#ffffff' },
};

/**
 * Draw a character with feet at (x, y). Parts are listed once, outlined in a first
 * pass and filled in a second, which gives the clean 1px silhouette outline.
 */
/** Draw a character with feet at (x, y). */
export function drawCharacter(c: Ctx, look: CharLook, x: number, y: number, pose: Pose): void {
  // Snap to the device-pixel grid so sprites never shimmer between pixels while moving.
  drawCharacterHD(c, look, Math.round(x * 2) / 2, Math.round(y * 2) / 2, pose);
}

/** The original blocky renderer, kept for reference/tests. */
export function drawCharacterPixel(c: Ctx, look: CharLook, x: number, y: number, pose: Pose): void {
  x = Math.round(x);
  y = Math.round(y);
  if (pose.down) return drawLying(c, look, x, y);

  const legH = look.small ? 4 : 6;
  const torsoH = look.small ? 8 : 10;
  const step = pose.moving ? Math.sin(pose.phase * Math.PI * 2) : 0;
  const bob = pose.moving ? (Math.abs(step) > 0.5 ? -1 : 0) : pose.breath > 0.5 ? -1 : 0;
  const f = pose.facing;
  const side = f === 'left' || f === 'right';
  const dir = f === 'left' ? -1 : 1;

  shadow(c, x, y - 1, look.small ? 6 : 8, 2.6);

  const parts: Part[] = [];
  const top = y - legH - torsoH - 3 + bob; // torso top
  const headTop = top - 12;

  // Legs & shoes.
  const lift = Math.round(step * 1.5);
  if (side) {
    const fwd = Math.round(step * 2);
    parts.push([look.bottom, x - 2 + fwd, y - legH - 2, 3, legH], [look.shoes, x - 2 + fwd + (dir > 0 ? 0 : -1), y - 3, 4, 3]);
    parts.push([look.bottom, x - 1 - fwd, y - legH - 2, 3, legH], [look.shoes, x - 1 - fwd + (dir > 0 ? 0 : -1), y - 3, 4, 3]);
  } else {
    parts.push([look.bottom, x - 4, y - legH - 2 - Math.max(0, lift), 3, legH], [look.shoes, x - 5, y - 3 - Math.max(0, lift), 4, 3]);
    parts.push([look.bottom, x + 1, y - legH - 2 - Math.max(0, -lift), 3, legH], [look.shoes, x + 1, y - 3 - Math.max(0, -lift), 4, 3]);
  }

  // Torso (dress flares out at the bottom).
  if (look.dress) {
    parts.push([look.top, x - 6, top, 12, torsoH - 2], [look.bottom, x - 7, top + torsoH - 4, 14, 5]);
  } else {
    parts.push([look.top, x - 6, top, 12, torsoH - 2], [look.bottom, x - 6, top + torsoH - 3, 12, 3]);
  }

  // Arms swing opposite to legs.
  const swing = Math.round(step * 1.5);
  if (side) {
    parts.push([look.top, x - 1 - swing * dir, top + 1, 3, 7], [look.skin, x - 1 - swing * dir, top + 8, 3, 2]);
  } else {
    parts.push([look.top, x - 8, top + 1 + Math.max(0, swing), 2, 7], [look.skin, x - 8, top + 8 + Math.max(0, swing), 2, 2]);
    parts.push([look.top, x + 6, top + 1 + Math.max(0, -swing), 2, 7], [look.skin, x + 6, top + 8 + Math.max(0, -swing), 2, 2]);
  }

  // Head.
  parts.push([look.skin, x - 6, headTop, 12, 12]);
  hairParts(parts, look, x, headTop, f);

  for (const [, px, py, w, h] of parts) {
    c.fillStyle = OUTLINE;
    c.fillRect(px - 1, py - 1, w + 2, h + 2);
  }
  for (const [col, px, py, w, h] of parts) {
    c.fillStyle = col;
    c.fillRect(px, py, w, h);
  }

  // Shading and details on top of the fills.
  c.fillStyle = look.topShade;
  c.fillRect(x - 6, top + torsoH - 4, 12, 1);
  if (!side) {
    c.fillRect(x + 4, top, 2, torsoH - 2);
    c.fillStyle = 'rgba(255,255,255,0.22)';
    c.fillRect(x - 5, top + 1, 2, torsoH - 4);
  } else {
    c.fillRect(x - 6 * dir + (dir > 0 ? 0 : 4), top, 2, torsoH - 2);
  }
  if (f === 'down' && !look.apron) {
    // Collar.
    c.fillStyle = look.top === '#f4f1ea' ? '#2f4a8a' : shadeHex(look.top, 0.35);
    c.fillRect(x - 2, top, 4, 1);
    c.fillRect(x - 1, top + 1, 2, 1);
  }
  c.fillStyle = 'rgba(255,255,255,0.35)';
  c.fillRect(side ? x - 1 : x - 4, y - 3 - Math.max(0, lift), 1, 1);
  c.fillRect(side ? x : x + 2, y - 3 - Math.max(0, -lift), 1, 1);
  if (look.hairStyle !== 'cap') {
    c.fillStyle = 'rgba(255,255,255,0.28)';
    c.fillRect(f === 'right' ? x - 3 : x - 4, headTop - 1, 4, 1);
  }
  if (look.apron && f !== 'up') {
    c.fillStyle = look.apron;
    c.fillRect(x - 4, top + 3, 8, torsoH - 1);
  }
  if (look.tie && f === 'down') {
    c.fillStyle = look.tie;
    c.fillRect(x - 1, top + 1, 2, 6);
  }
  if (look.badge && f === 'down') {
    c.fillStyle = look.badge;
    c.fillRect(x + 2, top + 2, 2, 2);
  }
  face(c, look, x, headTop, f, pose.blink);
}

function hairParts(parts: Part[], look: CharLook, x: number, top: number, f: Facing): void {
  const h = look.hair;
  const side = f === 'left' || f === 'right';
  const back = f === 'up';
  const d = f === 'left' ? -1 : 1;
  switch (look.hairStyle) {
    case 'cap':
      parts.push([look.hat ?? h, x - 7, top - 2, 14, 5]);
      parts.push([look.hat ?? h, side ? x + (d > 0 ? 2 : -9) : x - 5, top + 2, side ? 7 : 10, 2]);
      parts.push([h, x - 6, top + 3, 2, 4], [h, x + 4, top + 3, 2, 4]);
      return;
    case 'bun':
      parts.push([h, x - 6, top - 1, 12, 4], [h, x - 7, top + 1, 2, 6], [h, x + 5, top + 1, 2, 6]);
      parts.push([h, side ? x - 3 * d - 2 : x - 3, top - 5, 6, 5]);
      if (back) parts.push([h, x - 6, top, 12, 10]);
      return;
    case 'long':
      parts.push([h, x - 7, top - 1, 14, 5], [h, x - 7, top + 1, 3, 13], [h, x + 4, top + 1, 3, 13]);
      if (back) parts.push([h, x - 7, top, 14, 14]);
      if (side) parts.push([h, x - 7 * d - (d > 0 ? 0 : 3), top + 1, 4, 13]);
      return;
    case 'curly':
      parts.push([h, x - 8, top - 2, 16, 6], [h, x - 8, top + 2, 3, 6], [h, x + 5, top + 2, 3, 6]);
      if (back) parts.push([h, x - 7, top, 14, 11]);
      return;
    case 'pony':
      parts.push([h, x - 7, top - 1, 14, 5], [h, x - 7, top + 1, 2, 5], [h, x + 5, top + 1, 2, 5]);
      parts.push([h, side ? x - 9 * d - (d > 0 ? 0 : 2) : x + 5, top + 3, 4, 9]);
      if (back) parts.push([h, x - 6, top, 12, 10], [h, x - 2, top + 8, 4, 8]);
      return;
    case 'pigtails':
      parts.push([h, x - 6, top - 1, 12, 4], [h, x - 10, top + 3, 4, 6], [h, x + 6, top + 3, 4, 6]);
      if (back) parts.push([h, x - 6, top, 12, 10]);
      return;
    case 'messy':
      parts.push([h, x - 7, top - 2, 14, 5], [h, x - 4, top - 4, 3, 3], [h, x + 2, top - 3, 3, 2], [h, x - 7, top + 2, 2, 5], [h, x + 5, top + 2, 2, 5]);
      if (back) parts.push([h, x - 6, top, 12, 10]);
      return;
    case 'neat':
      parts.push([h, x - 6, top - 1, 12, 4], [h, x - 7, top + 1, 2, 4], [h, x + 5, top + 1, 2, 4]);
      if (back) parts.push([h, x - 6, top, 12, 9]);
      return;
    case 'short':
    default:
      parts.push([h, x - 7, top - 2, 14, 5], [h, x - 7, top + 1, 2, 6], [h, x + 5, top + 1, 2, 6]);
      if (back) parts.push([h, x - 6, top, 12, 10]);
      return;
  }
}

function face(c: Ctx, look: CharLook, x: number, top: number, f: Facing, blink: boolean): void {
  if (f === 'up') return;
  const eyeY = top + 6;
  const eye = (ex: number) => {
    c.fillStyle = '#1b1420';
    if (blink) c.fillRect(ex, eyeY + 1, 2, 1);
    else {
      c.fillRect(ex, eyeY, 2, 2);
      c.fillStyle = '#ffffff';
      c.fillRect(ex, eyeY, 1, 1);
    }
  };
  if (f === 'down') {
    eye(x - 4);
    eye(x + 2);
    c.fillStyle = 'rgba(255,120,120,0.45)';
    c.fillRect(x - 5, eyeY + 3, 2, 1);
    c.fillRect(x + 3, eyeY + 3, 2, 1);
    c.fillStyle = '#b5554a';
    c.fillRect(x - 1, eyeY + 4, 2, 1);
    if (look.glasses) {
      c.strokeStyle = '#2a1810';
      c.lineWidth = 0.75;
      c.strokeRect(x - 5, eyeY - 1, 4, 4);
      c.strokeRect(x + 1, eyeY - 1, 4, 4);
    }
  } else {
    const d = f === 'left' ? -1 : 1;
    eye(x + d * 3 - (d < 0 ? 1 : 0));
    c.fillStyle = 'rgba(255,120,120,0.45)';
    c.fillRect(x + d * 2 - (d < 0 ? 1 : 0), eyeY + 3, 2, 1);
    if (look.glasses) {
      c.strokeStyle = '#2a1810';
      c.lineWidth = 0.75;
      c.strokeRect(x + d * 3 - 1 - (d < 0 ? 1 : 0), eyeY - 1, 4, 4);
    }
  }
}

function drawLying(c: Ctx, look: CharLook, x: number, y: number): void {
  shadow(c, x, y - 2, 14, 3);
  const parts: Part[] = [
    [look.bottom, x + 2, y - 7, 10, 5],
    [look.shoes, x + 12, y - 7, 3, 5],
    [look.top, x - 8, y - 8, 11, 6],
    [look.skin, x - 18, y - 10, 10, 9],
    [look.hair, x - 19, y - 11, 4, 10],
  ];
  for (const [, px, py, w, h] of parts) {
    c.fillStyle = OUTLINE;
    c.fillRect(px - 1, py - 1, w + 2, h + 2);
  }
  for (const [col, px, py, w, h] of parts) {
    c.fillStyle = col;
    c.fillRect(px, py, w, h);
  }
  c.fillStyle = '#1b1420';
  c.fillRect(x - 12, y - 7, 2, 1);
}

/** The park cat (also used, smaller, for the lost kitten). */
export function drawCat(c: Ctx, x: number, y: number, pose: Pose, t: number, kitten = false, fur = '#f0a35e'): void {
  x = Math.round(x);
  y = Math.round(y);
  const k = kitten ? 0.75 : 1;
  shadow(c, x, y - 1, 7 * k, 2.2);
  const d = pose.facing === 'left' ? -1 : 1;
  const step = pose.moving ? Math.round(Math.sin(pose.phase * Math.PI * 2)) : 0;
  const parts: Part[] = [
    [fur, x - 6 * k, y - 8 * k, 12 * k, 6 * k],
    [fur, x - 5 * k + step, y - 3, 2, 3],
    [fur, x + 3 * k - step, y - 3, 2, 3],
    [fur, x + d * 5 * k - 4, y - 13 * k, 8, 7],
    [fur, x + d * 5 * k - 4, y - 15 * k, 2, 2],
    [fur, x + d * 5 * k + 2, y - 15 * k, 2, 2],
  ];
  const tail = Math.round(Math.sin(t * 3) * 2);
  parts.push([fur, x - d * 7 * k - (d > 0 ? 2 : 0), y - 12 * k + tail, 2, 6]);
  for (const [, px, py, w, h] of parts) {
    c.fillStyle = OUTLINE;
    c.fillRect(px - 1, py - 1, w + 2, h + 2);
  }
  for (const [col, px, py, w, h] of parts) {
    c.fillStyle = col;
    c.fillRect(px, py, w, h);
  }
  c.fillStyle = '#ffffff';
  c.fillRect(x - 3 * k, y - 4 * k, 6 * k, 2);
  c.fillStyle = 'rgba(120,60,20,0.45)';
  c.fillRect(x - 2 * k, y - 8 * k, 1, 4);
  c.fillRect(x + 1 * k, y - 8 * k, 1, 4);
  c.fillStyle = '#1b1420';
  const ex = x + d * 5 * k - 2;
  if (!pose.blink) {
    c.fillRect(ex, y - 11 * k, 1, 2);
    c.fillRect(ex + 3, y - 11 * k, 1, 2);
  } else {
    c.fillRect(ex, y - 10 * k, 1, 1);
    c.fillRect(ex + 3, y - 10 * k, 1, 1);
  }
  c.fillStyle = '#ff9fb0';
  c.fillRect(ex + 1, y - 9 * k, 2, 1);
}
